import type { ObservationDto, VersionMetrics } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, ErrorText } from '../../ui';

const VERDICT: Record<string, { label: string; tone: 'green' | 'red' | 'gray' | 'amber' }> = {
  migliore: { label: 'la variante va meglio', tone: 'green' },
  peggiore: { label: 'la variante va peggio', tone: 'red' },
  equivalente: { label: 'nessuna differenza chiara', tone: 'gray' },
  insufficiente: { label: 'servono più esecuzioni', tone: 'amber' },
};

const fmt = (v: number | null, suffix = '') => (v === null ? '—' : `${v}${suffix}`);

function Column({ title, m }: { title: string; m: VersionMetrics }) {
  return (
    <div className="rounded-lg border border-black/10 p-3 text-sm">
      <p className="mb-2 font-medium">
        {title} <span className="text-ink/45">· v{m.version}</span>
      </p>
      <dl className="grid grid-cols-2 gap-y-1">
        <dt className="text-ink/55">Esecuzioni</dt>
        <dd>
          {m.runs_closed}/{m.runs} chiuse
        </dd>
        <dt className="text-ink/55">Rifacimenti</dt>
        <dd>{m.rework_rate === null ? '—' : `${Math.round(m.rework_rate * 1000) / 10}%`}</dd>
        <dt className="text-ink/55">Durata media</dt>
        <dd>{fmt(m.avg_duration_minutes, ' min')}</dd>
        <dt className="text-ink/55">Segnalazioni</dt>
        <dd>{fmt(m.exceptions_per_run)} per esecuzione</dd>
        <dt className="text-ink/55">Riassegnazioni</dt>
        <dd>{fmt(m.reassignments_per_run)} per esecuzione</dd>
      </dl>
    </div>
  );
}

/** Variante in osservazione accanto alla versione attuale: confronto e decisione (promuovi o scarta). */
export function ObservationPanel({ processId, canEdit }: { processId: string; canEdit?: boolean }) {
  const { tenant, t, isManager } = useTenant();
  const allowed = canEdit ?? isManager;
  const queryClient = useQueryClient();
  const obs = useTenantQuery<ObservationDto>(['observation', processId], `/processes/${processId}/observation`);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] });
  const promote = useMutation({ mutationFn: () => api(t(`/processes/${processId}/observation/promote`), { method: 'POST' }), onSuccess: refresh });
  const rollback = useMutation({ mutationFn: () => api(t(`/processes/${processId}/observation/rollback`), { method: 'POST' }), onSuccess: refresh });
  const d = obs.data;
  if (!d?.observation || !d.active) return null;
  const verdict = d.verdict ? VERDICT[d.verdict] : null;
  return (
    <Card className="mb-6 border-blue-200 bg-blue-50/40">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="flex-1 text-sm font-semibold">Variante in osservazione dal {dateTime(d.since)}</h2>
        {verdict && <Badge tone={verdict.tone}>{verdict.label}</Badge>}
      </div>
      <p className="mb-3 text-xs text-ink/60">Le esecuzioni si alternano tra la versione attuale e la variante. Per giudicare servono almeno {d.min_runs} esecuzioni chiuse per ciascuna.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Column title="Attuale" m={d.active} />
        <Column title="Variante" m={d.observation} />
      </div>
      {allowed && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button disabled={promote.isPending} onClick={() => promote.mutate()}>
            Adotta la variante
          </Button>
          <Button variant="ghost" disabled={rollback.isPending} onClick={() => confirm('Scartare la variante e tornare solo alla versione attuale?') && rollback.mutate()}>
            Scarta la variante
          </Button>
        </div>
      )}
      <ErrorText>{promote.error ? errorMessage(promote.error) : rollback.error ? errorMessage(rollback.error) : ''}</ErrorText>
    </Card>
  );
}
